import React, {
  ElementType,
  ReactElement,
  ReactNode,
  useCallback,
  useState,
} from "react";

import styled, { createGlobalStyle } from "styled-components";

import {
  Connection,
  ConnectRepositoryDialog,
  Repository,
} from "./ConnectRepositoryDialog";
import { RoundAvatar } from "./RoundAvatar";
import { AVATAR_COLORS, MD, SM, TRANSITION } from "./tokens";
import {
  WEBHOOK_CONNECTIONS,
  WEBHOOK_REPOSITORIES,
  WebhookLogsDialog,
} from "./WebhookLogsDialog";
import { Badge } from "../../components/Badge";
import { Button } from "../../components/Button";
import { GlobalAddButton } from "../../components/Button/GlobalAddButton";
import { CompleteCheckbox } from "../../components/CompleteCheckbox";
import { CounterButton } from "../../components/CounterButton";
import { Dialog } from "../../components/Dialog";
import { Header } from "../../components/Header";
import { IconButton } from "../../components/IconButton";
import {
  AccessLogIcon,
  AddCrossTinyIcon,
  ArrowLeftIcon,
  CaretRightIcon,
  CheckboxBlankTogglerIcon,
  CloseSmallIcon,
  CrownBlankIcon,
  CrownSelectedIcon,
  DownloadIcon,
  DuplicateIcon,
  EditIcon,
  EditMultipleIcon,
  FilterIcon,
  GitHubIcon,
  GitIcon,
  GitLabIcon,
  GitRepositoryConnectIcon,
  GitRepositoryIcon,
  InfoIcon,
  InsertLinkIcon,
  LabelsIcon,
  NotificationBellIcon,
  PeopleIcon,
  ProjectTemplateConvertIcon,
  ProjectTemplateIcon,
  StarIcon,
  TrashIcon,
  TreeDotsIcon,
  ViewListIcon,
  ViewTimelineIcon,
} from "../../components/Icons";
import { List, ListItem, ListSeparator } from "../../components/List";
import { Menu, MenuHeader } from "../../components/Menu";
import { Item, Nav } from "../../components/Nav";
import { ProgressPie } from "../../components/ProgressPie";
import { ToastMessage } from "../../components/ToastMessage";
import { Tooltip } from "../../components/Tooltip";
import {
  ResizeTransition,
  SlideFromTop,
  SlideLeftRightTransition,
} from "../../components/Transitions";
import { Body2, Caption1, Header2, Title2 } from "../../components/Typography";

/** Gold used for the project-leader cues (a data colour, not a theme token). */
const LEADER_GOLD = "#FFCC6B";

/* ------------------------------------------------------------------ */
/* Page header row (mirrors PageHeader.jsx)                            */
/* ------------------------------------------------------------------ */

const StyledPageHeader = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin: 12px 0;
  width: 100%;
  height: 40px;
  flex-shrink: 0;

  .page-header-left {
    display: flex;
    align-items: center;
    width: 100%;
    max-width: 100%;

    @media (min-width: ${SM}) {
      min-width: 0;
    }
  }

  /* back button spacing (tw-shrink-0 tw-mr-1 md:tw-mr-2) */
  .back-button {
    flex-shrink: 0;
    margin-right: 4px;

    @media (min-width: ${MD}) {
      margin-right: 8px;
    }
  }

  .page-title-desktop {
    display: none;
    margin-right: 8px;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    @media (min-width: ${MD}) {
      display: block;
    }
  }

  .page-title-mobile {
    display: block;
    margin-right: 8px;
    font-weight: 300;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;

    @media (min-width: ${MD}) {
      display: none;
    }
  }

  .page-header-children {
    flex-shrink: 0;
    display: flex;
    align-items: center;
  }

  /* spacer that keeps the header clear of the absolutely positioned
     top-right controls, same as in the app (tw-w-52) */
  .page-header-spacer {
    display: none;
    height: 100%;
    width: 208px;
    flex-shrink: 0;

    @media (min-width: ${SM}) {
      display: flex;
    }
  }
`;

export interface PageHeaderProps {
  title: string;
  children?: ReactNode;
}

/** Generic page header (title + optional controls), mirrors PageHeader.jsx. */
export const PageHeader = ({
  title,
  children,
}: PageHeaderProps): ReactElement => (
  <StyledPageHeader>
    <div className="page-header-left">
      <Title2 className="page-title-desktop">{title}</Title2>
      <Header2 className="page-title-mobile">{title}</Header2>
      <div className="page-header-children">{children}</div>
    </div>
    <div className="page-header-spacer" />
  </StyledPageHeader>
);

PageHeader.displayName = "PageHeader";

/* ------------------------------------------------------------------ */
/* Project page header (mirrors ProjectPage.js Page.Header children)   */
/* ------------------------------------------------------------------ */

const StyledProjectHeaderChildren = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;

  .project-progress {
    display: flex;
    align-items: center;
    margin-left: 4px;
  }
`;

/* Project "..." options menu (Menu + List with leading-icon items). Two items
   (Favorite, Subscribe) have a checkbox on the right. Clicks are no-ops. */
const StyledProjectMenu = styled.div`
  .toggle-item {
    justify-content: space-between;
  }

  .toggle-item .opt-left {
    display: flex;
    align-items: center;
    min-width: 0;
  }

  .toggle-item .opt-left svg {
    margin-right: 8px;
    flex-shrink: 0;
  }

  /* the app renders the toggle as a small rounded (not circular) CompleteCheckbox */
  .toggle-item .c-complete-checkbox {
    width: 16px;
    height: 16px;
    border-radius: 3px;
    align-self: center;
    flex-shrink: 0;
  }

  /* "Repositories" item: caret pushed to the right edge */
  .submenu-item {
    justify-content: space-between;
  }

  /* right side of the "Repositories" item: connected-count badge + caret */
  .submenu-item .repo-right {
    display: flex;
    align-items: center;
    gap: 8px;
    flex-shrink: 0;
  }

  /* muted count badge — a quiet neutral pill rather than the bold primary
     default, so it informs without pulling focus */
  .submenu-item .repo-right .c-badge {
    background-color: var(--color-theme-300);
    color: var(--color-theme-700);
  }

  .submenu-item .repo-caret {
    flex-shrink: 0;
    fill: var(--color-theme-600);
  }

  /* a connected-repository row inside the submenu — long "path (Connection,
     Type)" labels truncate with an ellipsis rather than wrapping */
  .repo-row {
    min-width: 0;
  }

  .repo-row > svg {
    flex-shrink: 0;
  }

  .repo-name {
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }
`;

/* The project "..." menu shares the Webhook Logs dialog's repository /
 * connection universe, so a repository connected here is one that dialog can
 * pre-filter by. `WebhookConnection` and `Connection` share a shape, and a
 * `WebhookRepository`'s `path` maps onto a `Repository`'s `name`. */
const PROJECT_MENU_CONNECTIONS: Connection[] = WEBHOOK_CONNECTIONS;
const PROJECT_MENU_REPOSITORIES: Repository[] = WEBHOOK_REPOSITORIES.map(
  (repo) => ({ id: repo.id, name: repo.path, connectionId: repo.connectionId })
);

/** Submenu row label for a connected repository: "path (Connection)". The
 *  service type is omitted because the leading provider icon already conveys
 *  it. */
const repositoryMenuLabel = (repo: Repository): string => {
  const connection = PROJECT_MENU_CONNECTIONS.find(
    (c) => c.id === repo.connectionId
  );
  return connection ? `${repo.name} (${connection.name})` : repo.name;
};

/** Provider glyph for a connected repository: GitHub / GitLab for those
 *  services, and the generic Git mark for anything else (e.g. an upcoming
 *  Bitbucket connection). */
const repositoryIcon = (repo: Repository): ElementType => {
  const connection = PROJECT_MENU_CONNECTIONS.find(
    (c) => c.id === repo.connectionId
  );
  switch (connection?.service) {
    case "github":
      return GitHubIcon;
    case "gitlab":
      return GitLabIcon;
    default:
      return GitIcon;
  }
};

/** Width of the project "..." menu (matches the DS "normal" menu mode). */
const PROJECT_MENU_WIDTH = 260;

/* The menu drills in place (index → Repositories), so the paper must clip the
   sliding panels and give them a positioning context. */
const ProjectMenuSliderStyle = createGlobalStyle`
  .c-simple-menu__paper.project-options-menu {
    overflow: hidden;
    position: relative;
  }
`;

/* Toast anchor — fixed, upper-right corner (the toast slides down from the
   top). The wrapper ignores pointer events so it never blocks the page; only
   the toast itself is interactive. */
const StyledToastAnchor = styled.div`
  position: fixed;
  top: 16px;
  right: 16px;
  z-index: 10000;
  pointer-events: none;

  .c-toast-message {
    pointer-events: auto;
  }
`;

export interface ProjectOptionsMenuProps {
  /** When the `source` module is loaded, show the Repositories section. */
  sourceModuleLoaded?: boolean;
  /** Repositories already connected to the project when the menu first mounts. */
  initialConnectedRepositories?: Repository[];
  /**
   * Whether the current user can manage the project's repositories. Gates the
   * Webhook Logs entry (a manage-only action) — see note 09, Permissions.
   */
  canManageProjectRepositories?: boolean;
}

/**
 * The project "..." options menu. When the `source` module is loaded it gains
 * a **Repositories** item that, instead of flying out to the side, **drills in
 * within the same menu** (the pattern used across the app — see `Filter`): the
 * index slides out to the left and the Repositories view (a back-arrow header,
 * the connected repositories sorted by name, and a **Connect Repository**
 * action) slides in. Connecting adds a repository to the list; clicking one
 * would, in production, open it in a new tab (surfaced here via a dialog).
 */
const ProjectOptionsMenu = ({
  sourceModuleLoaded = false,
  initialConnectedRepositories = [],
  canManageProjectRepositories = true,
}: ProjectOptionsMenuProps): ReactElement => {
  const [open, setOpen] = useState(false);
  const [view, setView] = useState<"index" | "repositories">("index");
  const [webhookLogsOpen, setWebhookLogsOpen] = useState(false);
  const [isLeft, setIsLeft] = useState(true);
  const [height, setHeight] = useState(0);
  // Connected repositories drive the count badge on the Repositories item. In
  // production this count comes from the project's `count_repositories` counter,
  // kept current via pusher/websocket events, so the badge reflects repositories
  // connected or disconnected by anyone — not just the current user's actions.
  const [connectedRepos, setConnectedRepos] = useState<Repository[]>(
    initialConnectedRepositories
  );
  const [connectOpen, setConnectOpen] = useState(false);
  const [infoOpen, setInfoOpen] = useState(false);
  const [toastOpen, setToastOpen] = useState(false);
  // Bumped on each connect so the toast remounts and its auto-hide timer
  // restarts from the moment it is shown.
  const [toastKey, setToastKey] = useState(0);

  // Repositories still available to connect (not already on the project).
  const connectableRepos = PROJECT_MENU_REPOSITORIES.filter(
    (r) => !connectedRepos.some((c) => c.id === r.id)
  );

  // Connected repositories are listed in the submenu sorted by name.
  const sortedRepos = [...connectedRepos].sort((a, b) =>
    a.name.localeCompare(b.name)
  );

  // Each view measures itself as it mounts so the paper can animate its height.
  const measure = useCallback((node: HTMLDivElement | null) => {
    if (node) {
      setHeight(node.offsetHeight);
    }
  }, []);

  const goToRepositories = (): void => {
    setView("repositories");
    setIsLeft(true);
  };

  const backToIndex = (): void => {
    setView("index");
    setIsLeft(false);
  };

  const handleClose = (): void => {
    setOpen(false);
    // Reset to the index so the next open starts at the top level.
    setView("index");
    setIsLeft(true);
  };

  const handleConnected = (repo: Repository): void => {
    setConnectedRepos((prev) =>
      prev.some((r) => r.id === repo.id) ? prev : [...prev, repo]
    );
    setToastOpen(true);
    setToastKey((k) => k + 1);
  };

  const handleConnect = (): void => {
    handleClose();
    setConnectOpen(true);
  };

  // In production this closes the menu and opens the repository in a new tab;
  // in the mock we surface that behaviour in a small dialog instead.
  const handleOpenRepository = (): void => {
    handleClose();
    setInfoOpen(true);
  };

  const handleOpenWebhookLogs = (): void => {
    handleClose();
    setWebhookLogsOpen(true);
  };

  const indexView = (
    <StyledProjectMenu>
      <List style={{ padding: "8px 0" }} tabIndex={-1}>
        <ListItem>
          <EditIcon />
          Edit
        </ListItem>
        <ListItem className="toggle-item">
          <span className="opt-left">
            <StarIcon />
            Mark as Favorite
          </span>
          <CompleteCheckbox primary completed />
        </ListItem>
        <ListItem className="toggle-item">
          <span className="opt-left">
            <NotificationBellIcon />
            Subscribe to Everything
          </span>
          <CompleteCheckbox primary completed={false} />
        </ListItem>
        <ListItem>
          <LabelsIcon />
          Manage Task Labels
        </ListItem>
        <ListItem>
          <InsertLinkIcon />
          Copy Link
        </ListItem>
        <ListSeparator />
        <ListItem>
          <CheckboxBlankTogglerIcon />
          Complete
        </ListItem>
        <ListItem>
          <DownloadIcon />
          Export
        </ListItem>
        {sourceModuleLoaded ? (
          <>
            <ListSeparator />
            <ListItem
              className="toggle-item submenu-item"
              onClick={goToRepositories}
            >
              <span className="opt-left">
                <GitRepositoryIcon />
                Repositories
              </span>
              <span className="repo-right">
                {connectedRepos.length > 0 ? (
                  <Badge
                    isStandalone
                    dimension={16}
                    value={connectedRepos.length}
                  />
                ) : null}
                <CaretRightIcon className="repo-caret" width={16} height={16} />
              </span>
            </ListItem>
          </>
        ) : null}
        <ListSeparator />
        <ListItem>
          <DuplicateIcon />
          Duplicate
        </ListItem>
        <ListItem>
          <ProjectTemplateIcon />
          Apply Template
        </ListItem>
        <ListItem>
          <ProjectTemplateConvertIcon />
          Convert to Template
        </ListItem>
        <ListSeparator />
        <ListItem>
          <TrashIcon />
          Delete
        </ListItem>
      </List>
    </StyledProjectMenu>
  );

  return (
    <>
      <ProjectMenuSliderStyle />
      <Menu
        open={open}
        onOpen={() => setOpen(true)}
        onClose={handleClose}
        position="bottom-end"
        menuClassName={sourceModuleLoaded ? "project-options-menu" : undefined}
        target={
          <IconButton type="button" variant="text gray" active={open}>
            <TreeDotsIcon />
          </IconButton>
        }
      >
        {sourceModuleLoaded ? (
          <ResizeTransition in style={{ height, width: PROJECT_MENU_WIDTH }}>
            <div style={{ position: "relative", overflow: "hidden" }}>
              <SlideLeftRightTransition
                in={view === "index"}
                direction={isLeft ? "left" : "right"}
              >
                <div ref={measure} style={{ width: PROJECT_MENU_WIDTH }}>
                  {indexView}
                </div>
              </SlideLeftRightTransition>
              <SlideLeftRightTransition
                in={view === "repositories"}
                direction={isLeft ? "left" : "right"}
              >
                <div ref={measure} style={{ width: PROJECT_MENU_WIDTH }}>
                  <MenuHeader
                    title="Repositories"
                    leftElement={
                      <IconButton
                        type="button"
                        variant="text gray"
                        size="small"
                        onClick={backToIndex}
                        aria-label="Back"
                      >
                        <ArrowLeftIcon />
                      </IconButton>
                    }
                  />
                  <StyledProjectMenu>
                    <List style={{ padding: "8px 0" }} tabIndex={-1}>
                      {sortedRepos.map((repo) => {
                        const RepoIcon = repositoryIcon(repo);
                        return (
                          <ListItem
                            key={repo.id}
                            className="repo-row"
                            onClick={() => handleOpenRepository()}
                          >
                            <RepoIcon />
                            <span className="repo-name">
                              {repositoryMenuLabel(repo)}
                            </span>
                          </ListItem>
                        );
                      })}
                      {sortedRepos.length > 0 ? <ListSeparator /> : null}
                      {sortedRepos.length > 0 &&
                      canManageProjectRepositories ? (
                        <ListItem onClick={handleOpenWebhookLogs}>
                          <AccessLogIcon />
                          Webhook Logs
                        </ListItem>
                      ) : null}
                      <ListItem onClick={handleConnect}>
                        <GitRepositoryConnectIcon />
                        Connect Repository
                      </ListItem>
                    </List>
                  </StyledProjectMenu>
                </div>
              </SlideLeftRightTransition>
            </div>
          </ResizeTransition>
        ) : (
          indexView
        )}
      </Menu>

      <ConnectRepositoryDialog
        open={connectOpen}
        onClose={() => setConnectOpen(false)}
        onConnect={handleConnected}
        repositories={connectableRepos}
        connections={PROJECT_MENU_CONNECTIONS}
      />

      <WebhookLogsDialog
        open={webhookLogsOpen}
        onClose={() => setWebhookLogsOpen(false)}
        repositoryIds={sortedRepos.map((repo) => repo.id)}
      />

      <Dialog
        open={infoOpen}
        onClose={() => setInfoOpen(false)}
        enableBackgroundClick
      >
        <Dialog.Title>Open Repository</Dialog.Title>
        <Dialog.ContentDivider />
        <Dialog.Content>
          <Body2>
            In production, this click would close the menu and open the
            repository in a new browser tab.
          </Body2>
        </Dialog.Content>
        <Dialog.ContentDivider />
        <Dialog.Actions>
          <Button variant="primary" onClick={() => setInfoOpen(false)}>
            Got it
          </Button>
        </Dialog.Actions>
      </Dialog>

      <StyledToastAnchor>
        <SlideFromTop in={toastOpen}>
          <ToastMessage
            key={toastKey}
            className="c-toast-message"
            type="success"
            text="Repository connected."
            dismissible
            dropShadow
            timeout={3000}
            onClose={() => setToastOpen(false)}
          />
        </SlideFromTop>
      </StyledToastAnchor>
    </>
  );
};

/* People popover (mirrors the project header People dropdown): a header row
   ("People" + add button), a "Members" section and a row per member. The
   project leader shows a gold crown; other members reveal a "set as leader"
   crown and a remove button on hover. Clicks are no-ops. */
interface ProjectMember {
  name: string;
  initials: string;
  color: string;
  leader?: boolean;
}

const PROJECT_MEMBERS: ProjectMember[] = [
  {
    name: "Theo Brandt",
    initials: "TB",
    color: AVATAR_COLORS.blue,
    leader: true,
  },
  { name: "Iris Calloway", initials: "IC", color: AVATAR_COLORS.purple },
  { name: "Account Director", initials: "AD", color: "#8A8F98" },
  { name: "Grace Bennett", initials: "GB", color: AVATAR_COLORS.orange },
];

const StyledPeopleMenu = styled.div`
  width: 256px;

  .members-label {
    display: block;
    margin: 8px 0;
    padding: 0 16px;
  }

  .member-row {
    position: relative;
    display: flex;
    align-items: center;
    min-height: 40px;
    margin: 0 8px;
    padding: 2px 8px;
    border-radius: 4px;
    cursor: default;
    transition: background-color ${TRANSITION};
  }

  .member-row:hover {
    background-color: var(--color-theme-200);
  }

  /* gold left bar on the project-leader row */
  .member-row.leader::before {
    content: "";
    position: absolute;
    top: 4px;
    bottom: 4px;
    left: 0;
    width: 4px;
    border-radius: 4px;
    background-color: ${LEADER_GOLD};
  }

  /* gold ring on the leader's avatar */
  .member-row.leader .member-avatar {
    outline: 2px solid ${LEADER_GOLD};
    outline-offset: -2px;
  }

  .member-name {
    flex: 1;
    margin-left: 12px;
    min-width: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;
  }

  .member-right {
    display: flex;
    align-items: center;
    gap: 2px;
    flex-shrink: 0;
    margin-left: 8px;
  }

  .leader-crown {
    flex-shrink: 0;
  }

  /* member actions (set-as-leader / remove) appear on hover, like the app */
  .member-options {
    display: none;
    align-items: center;
    gap: 2px;
  }

  .member-row:hover .member-options {
    display: flex;
  }
`;

const PeoplePopover = (): ReactElement => {
  const [open, setOpen] = useState(false);
  return (
    <Menu
      open={open}
      onOpen={() => setOpen(true)}
      onClose={() => setOpen(false)}
      position="bottom-end"
      target={
        <CounterButton
          label={String(PROJECT_MEMBERS.length)}
          icon={<PeopleIcon />}
          active={open}
          onClearAll={() => undefined}
        />
      }
    >
      <StyledPeopleMenu>
        <MenuHeader
          title="People"
          rightElement={
            <IconButton
              type="button"
              variant="text gray"
              size="small"
              aria-label="Add People"
            >
              <AddCrossTinyIcon />
            </IconButton>
          }
        />
        <Caption1 weight="bold" className="members-label">
          Members
        </Caption1>
        {PROJECT_MEMBERS.map((member) => (
          <div
            className={member.leader ? "member-row leader" : "member-row"}
            key={member.name}
          >
            <RoundAvatar
              className="member-avatar"
              $bg={member.color}
              $bordered={false}
            >
              {member.initials}
            </RoundAvatar>
            <Body2 className="member-name">{member.name}</Body2>
            <span className="member-right">
              {member.leader && (
                <CrownSelectedIcon
                  className="leader-crown"
                  style={{ fill: LEADER_GOLD }}
                />
              )}
              <span className="member-options">
                {!member.leader && (
                  <Tooltip title="Set as Project Leader">
                    <IconButton type="button" variant="text gray" size="small">
                      <CrownBlankIcon />
                    </IconButton>
                  </Tooltip>
                )}
                <Tooltip title="Remove from Project">
                  <IconButton type="button" variant="text gray" size="small">
                    <CloseSmallIcon />
                  </IconButton>
                </Tooltip>
              </span>
            </span>
          </div>
        ))}
      </StyledPeopleMenu>
    </Menu>
  );
};

export interface ProjectPageHeaderProps {
  title?: string;
  /** When the `source` module is loaded, the "..." menu shows a Repositories
   *  section (connected repositories + Connect Repository). */
  sourceModuleLoaded?: boolean;
  /** Repositories already connected to the project (seeds the count + submenu). */
  initialConnectedRepositories?: Repository[];
  /** Whether the current user can manage the project's repositories. */
  canManageProjectRepositories?: boolean;
}

/** The project page header: back arrow, title, progress, info, people, options. */
export const ProjectPageHeader = ({
  title = "#131: Ime projekta",
  sourceModuleLoaded = false,
  initialConnectedRepositories,
  canManageProjectRepositories,
}: ProjectPageHeaderProps): ReactElement => (
  <StyledPageHeader>
    <div className="page-header-left">
      <IconButton variant="text gray" className="back-button">
        <ArrowLeftIcon />
      </IconButton>
      <Title2 className="page-title-desktop">{title}</Title2>
      <Header2 className="page-title-mobile">{title}</Header2>
      <div className="page-header-children">
        <StyledProjectHeaderChildren>
          <span className="project-progress">
            <ProgressPie
              radius={11}
              progress={64}
              progressColor="var(--color-secondary)"
              backgroundColor="var(--color-theme-300)"
            />
          </span>
          <Tooltip title="Project Info">
            <IconButton variant="text gray">
              <InfoIcon />
            </IconButton>
          </Tooltip>
          <PeoplePopover />
          <ProjectOptionsMenu
            sourceModuleLoaded={sourceModuleLoaded}
            initialConnectedRepositories={initialConnectedRepositories}
            canManageProjectRepositories={canManageProjectRepositories}
          />
        </StyledProjectHeaderChildren>
      </div>
    </div>
    <div className="page-header-spacer" />
  </StyledPageHeader>
);

ProjectPageHeader.displayName = "ProjectPageHeader";

/* ------------------------------------------------------------------ */
/* Tab bar (mirrors the <Header> with <Nav> on ProjectPage.js)         */
/* ------------------------------------------------------------------ */

const NAV_TABS = ["Tasks", "Discussions", "Files", "Notes", "Activity"];

const StyledTabHeader = styled(Header)`
  justify-content: space-between;
  align-items: center;
  margin-bottom: 16px;
  min-height: 64px;
  flex-shrink: 0;
`;

const StyledTabControls = styled.div`
  display: flex;
  align-items: center;
  flex-shrink: 0;
  gap: 4px;

  .vertical-separator {
    width: 1px;
    height: 24px;
    background-color: var(--color-theme-400);
    margin: 0 8px;
  }

  .filter-button {
    display: flex;
    align-items: center;
    gap: 6px;
  }
`;

export interface TabHeaderProps {
  /** Which view-mode toggle is highlighted as active. */
  activeView?: "list" | "column" | "timeline";
}

/** The Tasks/Discussions/… tab bar plus the right-hand view toolbar. */
export const TabHeader = ({
  activeView = "list",
}: TabHeaderProps): ReactElement => {
  const [active, setActive] = useState(0);
  return (
    <StyledTabHeader>
      <Nav onSelect={setActive}>
        {NAV_TABS.map((label, index) => (
          <Item active={index === active} key={label} name={label}>
            <Body2 color="secondary" weight="medium">
              {label}
            </Body2>
          </Item>
        ))}
      </Nav>
      <StyledTabControls>
        <Tooltip title="Batch Edit">
          <IconButton variant="text gray">
            <EditMultipleIcon />
          </IconButton>
        </Tooltip>
        <span className="vertical-separator" />
        <Button variant="text gray" className="filter-button">
          <FilterIcon />
          Filter
        </Button>
        <Tooltip title="List view">
          <IconButton variant="text gray" active={activeView === "list"}>
            <ViewListIcon />
          </IconButton>
        </Tooltip>
        <Tooltip title="Column view">
          <IconButton variant="text gray" active={activeView === "column"}>
            <ViewListIcon style={{ transform: "rotate(90deg)" }} />
          </IconButton>
        </Tooltip>
        <Tooltip title="Timeline view">
          <IconButton variant="text gray" active={activeView === "timeline"}>
            <ViewTimelineIcon />
          </IconButton>
        </Tooltip>
        <span className="vertical-separator" />
        <Tooltip title="Add New">
          <GlobalAddButton />
        </Tooltip>
      </StyledTabControls>
    </StyledTabHeader>
  );
};

TabHeader.displayName = "TabHeader";
